import DropDownButtons from "components/Ui/DropDownButtons/DropDownButtons";
import TableBodyCells from "components/Ui/TableBodyCells/TableBodyCells";
import UserNote from "components/Ui/UserNote/UserNote";
import MenuSimple from "./Dropdown";
import { NavLink } from "react-router-dom";
import UpdateTime from "./updateTime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { faMapLocationDot } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { formatMoney } from "utils/money";
import OrderWalletCell, { formatSettledNote } from "components/Ui/OrderWalletCell/OrderWalletCell";
import { readApiError } from "utils/apiError";
import {
    formatPickupDelta,
    isPickupOnTime,
    minutesBetween,
    formatDuration,
    formatDateTime,
} from "utils/orderTiming";
import PrepTimer from "components/Ui/PrepTimer/PrepTimer";
import { ltrText } from "utils/dateFormat";

/**
 * One row of the /deliveryOrders driver ledger. Same order payload as the
 * /orders row (Order/Order/Order.js) rendered for a different job: settlement
 * review rather than order management. It is a reduced sibling, not a variant —
 * the two files share no code and drift independently.
 *
 * WHAT IS DELIBERATELY ABSENT compared with the /orders row: no type icon (the
 * id is a plain link), no driver select — assignment happens on /orders and
 * this cell is read-only text — no second-phone line, no rating, no isFree, no
 * print, no clipboard copy, no vendor re-ping and no edit. What remains is what
 * a settlement pass needs: who, which vendor, how much, status, prep time,
 * which driver, and both soft-delete flags.
 *
 * ALL ids link to /deliveryOrders/:id regardless of order type, because this
 * screen's audience does not care about cart lines.
 *
 * KNOWN CONSTRAINTS (documented, not fixed):
 * - The نوع الطلب cell is HARDCODED to "توصيل"; it never reads `order.lang`.
 *   The same order shows as "استلام" (pickup) on /orders, so a pickup order is
 *   mislabelled here.
 * - The hard "حذف" item is offered to EVERY role here, while the audience-safe
 *   "حذف للسائق فقط" is gated to non-vendors — the inverse of the /orders row,
 *   where the hard delete is the gated one.
 * - `updateTime` passes a third argument ("delivery") to `editOrder`, which
 *   accepts only two; it is ignored.
 */
const Order = (props) => {
    const adminInfo = useSelector((state) => state.auth.userData);
    const canEditNotes = !!(adminInfo?.super_admin || adminInfo?.roles?.manage_users);
    const updateTime = async (time) => {
        const fm = new FormData();
        fm.append("time", time);
        // The hook returns a failure rather than throwing it, so an ignored
        // return value is a silent failure. See utils/apiError.js.
        const failure = readApiError(
            await props.editOrder(props.order.id, fm, "delivery"),
            "فشل تحديث وقت التحضير",
        );
        if (failure) props.showMessage?.("error", "هناك خطأ", failure);
    };

    const cellRenderers = {
        id: () => (
            <td>
                <NavLink style={{ color: "blue" }} to={`/deliveryOrders/${props.order?.id}`}>
                    {props.order?.id}
                </NavLink>
            </td>
        ),
        name: () => (
            <td className="name">
                <span>
                    <div>
                        <h6 style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span>{props.order?.name}</span>
                            {props.order?.user && adminInfo?.role !== "vendor" ? (
                                <UserNote
                                    userId={props.order.user.id}
                                    note={props.order.user.note}
                                    userName={props.order.user.name || props.order?.name}
                                    editable={canEditNotes}
                                    showMessage={props.showMessage}
                                />
                            ) : null}
                        </h6>
                        <p>{props.order?.phone}</p>
                    </div>
                </span>
            </td>
        ),
        vendor: () => <td>{props.order?.vendor?.user?.name}</td>,
        total: () => <td>{formatMoney(props.order?.total)}</td>,
        status: () => (
            <td>
                <MenuSimple
                    id={props.order.id}
                    updateOrder={props.editOrder}
                    status={props.order?.status}
                    showMessage={props.showMessage}
                />
            </td>
        ),
        prepTimer: () => (
            <td>
                <PrepTimer order={props.order} />
            </td>
        ),
        prepTime: () => (
            <td>
                <UpdateTime updateTime={updateTime} value={props.order?.time} />
            </td>
        ),
        // The three timing/dispatch cells, identical to the orders page — see
        // components/Order/Order/Order.js for why each empty state is worded the
        // way it is. Kept as their own renderers rather than shared, matching
        // how the two tables already treat every other cell.
        pickupDelta: () => {
            const label = formatPickupDelta(props.order?.pickup_delta_seconds);

            if (!label) {
                return (
                    <td>
                        <span className="text-muted">
                            {props.order?.picked_up_at
                                ? "لا يوجد وقت تحضير"
                                : props.order?.deliveryId
                                  ? "لم يُسجَّل الاستلام"
                                  : "—"}
                        </span>
                    </td>
                );
            }

            return (
                <td>
                    <span
                        className={`order-timing ${
                            isPickupOnTime(props.order.pickup_delta_seconds)
                                ? "order-timing--early"
                                : "order-timing--late"
                        }`}
                        title={`استلمه السائق من المطعم: ${ltrText(formatDateTime(props.order?.picked_up_at))}`}
                    >
                        {label}
                    </span>
                </td>
            );
        },
        deliveryTime: () => {
            const label = formatDuration(
                minutesBetween(props.order?.assigned_at, props.order?.delivered_at),
            );

            return (
                <td>
                    {label ? (
                        <span
                            className="order-timing"
                            title={`سُلّم للزبون: ${ltrText(formatDateTime(props.order?.delivered_at))}`}
                        >
                            {label}
                        </span>
                    ) : (
                        <span className="text-muted">
                            {props.order?.status === "complete" ? "غير متاح" : "—"}
                        </span>
                    )}
                </td>
            );
        },
        /**
         * The road only: from the pickup at the restaurant to the customer. Next to
         * «من الإسناد حتى التسليم», which also counts the wait at the restaurant, so
         * the two together say where a slow delivery lost its time.
         */
        roadTime: () => {
            const minutes = minutesBetween(props.order?.picked_up_at, props.order?.delivered_at);
            const label = formatDuration(minutes);

            return (
                <td>
                    {label ? (
                        <span
                            className="order-timing"
                            title={`استلمه السائق من المطعم: ${ltrText(formatDateTime(props.order?.picked_up_at))} · سُلّم للزبون: ${ltrText(formatDateTime(props.order?.delivered_at))}`}
                        >
                            {label}
                        </span>
                    ) : (
                        <span className="text-muted">
                            {props.order?.status === "complete" && !props.order?.picked_up_at
                                ? "لم يُسجَّل الاستلام"
                                : "—"}
                        </span>
                    )}
                </td>
            );
        },
        area: () => <td>{props.order?.area?.name || <span className="text-muted">—</span>}</td>,
        shipping: () => <td>{formatMoney(props.order?.shipping)}</td>,
        delivery: () => (
            <td>{props.order?.deliveryId ? props.order.delivery?.user?.name : "لا يوجد"}</td>
        ),
        type: () => <td>{"توصيل"}</td>,
        createdAt: () => (
            <td>
                <bdi dir="ltr">{formatDateTime(props.order.createdAt)}</bdi>
            </td>
        ),
        deletedForVendor: () => (
            <td style={{ color: props.order.deleted_for_vendor ? "red" : "green" }}>
                {props.order.deleted_for_vendor ? "نعم" : "لا"}
            </td>
        ),
        deletedForDelivery: () => (
            <td style={{ color: props.order.deleted_for_delivery ? "red" : "green" }}>
                {props.order.deleted_for_delivery ? "نعم" : "لا"}
            </td>
        ),
        // THE TWO WALLETS, the same cell as the /orders table (admin#41): what
        // each wallet holds for this order, «—» when it holds nothing. The
        // driver cell keeps the settlement time this page is read for.
        vendorWallet: () => (
            <OrderWalletCell
                amount={props.order.vendor_wallet_commission}
                note={formatSettledNote(props.order.vendor_wallet_settled_at)}
            />
        ),
        driverWallet: () => (
            <OrderWalletCell
                amount={props.order.driver_wallet_fee}
                note={formatSettledNote(props.order.delivery_wallet_settled_at)}
            />
        ),
        actions: () => (
            <td
                style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    paddingTop: "10px",
                }}
            >
                {props.order?.phone && (
                    <a
                        href={`https://wa.me/972${props.order.phone.slice(
                            1,
                        )}?text="مرحبا طلبت طلبية من تطبيق طلباتك"`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <FontAwesomeIcon icon={faWhatsapp} size="xl" />
                    </a>
                )}
                {props.order?.location && (
                    <a href={props.order?.location} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faMapLocationDot} size="xl" color="red" />
                    </a>
                )}
                <DropDownButtons
                    vendor={props.vendor}
                    delete={() => {
                        props.setSelectedOrderValue(props.order);
                        props.toggleDelete();
                    }}
                    deleteForDelivery={
                        adminInfo.role !== "vendor"
                            ? () => {
                                  props.onRequestDeleteForDelivery?.(props.order);
                              }
                            : undefined
                    }
                    deleteForDeliveryDisabled={props.order.deleted_for_delivery}
                ></DropDownButtons>
            </td>
        ),
    };

    return (
        <tr className="item">
            <TableBodyCells visibleColumns={props.visibleColumns} renderers={cellRenderers} />
        </tr>
    );
};

export default Order;
